import { useEffect, useMemo, useState } from 'react';
import Icon from '../../shared/components/Icons';
import { formatPrice } from '../../shared/data/common';
import { getSeatMap } from '../../api/flights';

// Selections: { [segmentId]: { [travellerIndex]: { baggage?, meal?, seat? } } }
// where each choice is { code, amount, label }.
const kinds = [['seat', 'Seats'], ['meal', 'Meals'], ['baggage', 'Baggage']];

/** Flight legs with the add-on options returned by the supplier fare review. */
export function segmentsFromReview(reviewData) {
  return (reviewData?.tripInfos || []).flatMap((trip) => trip.sI || []).map((s) => ({
    id: String(s.id),
    label: `${s.da?.code} → ${s.aa?.code}`,
    flightNo: `${s.fD?.aI?.code || ''}-${s.fD?.fN || ''}`,
    // On connecting legs TripJack sends baggage without an amount: what is bought on the
    // first leg applies to the whole journey, so those legs offer no separate choice.
    baggage: (s.ssrInfo?.BAGGAGE || []).filter((o) => o.amount != null)
      .map((o) => ({ code: o.code, amount: Number(o.amount) || 0, label: o.desc || o.code })),
    journeyBaggage: (s.ssrInfo?.BAGGAGE || []).length > 0 && (s.ssrInfo?.BAGGAGE || []).every((o) => o.amount == null),
    meal: (s.ssrInfo?.MEAL || []).map((o) => ({ code: o.code, amount: Number(o.amount) || 0, label: o.desc || o.code })),
  }));
}

export function addonTotals(selections) {
  const totals = { seat: 0, meal: 0, baggage: 0 };
  Object.values(selections).forEach((byTraveller) => Object.values(byTraveller).forEach((choice) => {
    kinds.forEach(([kind]) => { totals[kind] += choice[kind]?.amount || 0; });
  }));
  return totals;
}

/** Supplier service selections for one traveller, for the checkout request. */
export function servicesFor(selections, travellerIndex) {
  return Object.entries(selections).flatMap(([segmentKey, byTraveller]) =>
    kinds.map(([kind]) => [kind, byTraveller[travellerIndex]?.[kind]])
      .filter(([, choice]) => choice)
      .map(([kind, choice]) => ({ type: kind, segmentKey, code: choice.code })));
}

function useSeatMap(supplierCode, bookingId, applicable) {
  const enabled = Boolean(bookingId) && applicable;
  const [state, setState] = useState({ loading: enabled, seats: {}, error: '' });
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    getSeatMap(supplierCode, bookingId, controller.signal)
      .then((response) => {
        const data = response?.data || response;
        setState({ loading: false, seats: data?.tripSeatMap?.tripSeat || {}, error: '' });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        const message = /not applicable/i.test(error.message || '')
          ? 'The airline does not offer seat selection for this fare. Seats are assigned free at check-in.'
          : 'We couldn\'t load the seat map. You can still book; seats are assigned at check-in.';
        setState({ loading: false, seats: {}, error: message });
      });
    return () => controller.abort();
  }, [supplierCode, bookingId, enabled]);
  return state;
}

function SeatMap({ map, travellers, active, selections, onPick }) {
  const layout = useMemo(() => {
    const byPos = new Map();
    const columns = new Map();
    map.sInfo.forEach((seat) => {
      const { row, column } = seat.seatPosition || {};
      byPos.set(`${row}-${column}`, seat);
      if (!columns.has(column)) columns.set(column, (seat.seatNo || '').replace(/^\d+/, ''));
    });
    const rows = [...new Set(map.sInfo.map((s) => s.seatPosition?.row))].sort((a, b) => a - b);
    const cols = Array.from({ length: map.sData?.column || Math.max(...columns.keys()) }, (_, i) => i + 1);
    return { byPos, columns, rows, cols };
  }, [map]);

  const takenBy = new Map();
  Object.entries(selections).forEach(([index, choice]) => { if (choice.seat) takenBy.set(choice.seat.code, Number(index)); });

  return (
    <div className="seatmap-wrap">
      <div className="seat-legend">
        <span><i className="seat-key free" /> Free</span>
        <span><i className="seat-key paid" /> Paid</span>
        <span><i className="seat-key xl" /> Extra legroom</span>
        <span><i className="seat-key booked" /> Taken</span>
        <span><i className="seat-key mine" /> Your selection</span>
      </div>
      <div className="seatmap" role="grid" aria-label="Seat map">
        <div className="seat-row seat-head" role="row">
          <span className="seat-rownum" />
          {layout.cols.map((c) => (layout.columns.has(c)
            ? <span key={c} className="seat-col">{layout.columns.get(c)}</span>
            : <span key={c} className="seat-aisle" />))}
        </div>
        {layout.rows.map((row) => (
          <div key={row} className="seat-row" role="row">
            <span className="seat-rownum">{row}</span>
            {layout.cols.map((col) => {
              if (!layout.columns.has(col)) return <span key={col} className="seat-aisle" />;
              const seat = layout.byPos.get(`${row}-${col}`);
              if (!seat) return <span key={col} className="seat-gap" />;
              const owner = takenBy.get(seat.code);
              const mine = owner != null;
              const features = [seat.isWindow && 'Window', seat.isAisle && 'Aisle', seat.isLegroom && 'Extra legroom', seat.isExitRow && 'Exit row'].filter(Boolean);
              const label = `${seat.seatNo} · ${seat.amount ? formatPrice(seat.amount) : 'Free'}${features.length ? ` · ${features.join(', ')}` : ''}${seat.isBooked ? ' · Taken' : ''}`;
              return (
                <button key={col} type="button" role="gridcell" title={label} aria-label={label}
                  disabled={seat.isBooked || (mine && owner !== active)}
                  aria-selected={mine && owner === active}
                  className={`seat ${seat.isBooked ? 'booked' : ''} ${seat.amount ? 'paid' : 'free'} ${seat.isLegroom || seat.isExitRow ? 'xl' : ''} ${mine ? 'mine' : ''}`}
                  onClick={() => onPick(owner === active ? null : { code: seat.code, amount: Number(seat.amount) || 0, label: seat.seatNo })}>
                  {mine ? travellers.findIndex((t) => t.index === owner) + 1 : ''}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function OptionCards({ options, value, onPick, noneLabel }) {
  return (
    <div className="addon-options">
      <button type="button" className={`addon-option ${!value ? 'active' : ''}`} aria-pressed={!value} onClick={() => onPick(null)}>
        <b>{noneLabel}</b><span>Included</span>
      </button>
      {options.map((o) => (
        <button key={o.code} type="button" className={`addon-option ${value?.code === o.code ? 'active' : ''}`}
          aria-pressed={value?.code === o.code} onClick={() => onPick(o)}>
          <b>{o.label}</b><span>{o.amount ? formatPrice(o.amount) : 'Free'}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Optional baggage, meals and seats for each flight leg and traveller (infants excluded).
 * Options come from the supplier review; the seat map is fetched for the reviewed booking id.
 */
export default function FlightAddons({ segments, travellers, value, onChange, supplierCode, bookingId, seatsApplicable = true }) {
  const seatMap = useSeatMap(supplierCode, bookingId, seatsApplicable);
  const [segmentId, setSegmentId] = useState(segments[0]?.id);
  const [kind, setKind] = useState('seat');
  const [active, setActive] = useState(travellers[0]?.index);
  const segment = segments.find((s) => s.id === segmentId) || segments[0];
  if (!segment || !travellers.length) return null;

  const map = seatMap.seats[segment.id];
  const available = {
    seat: Boolean(map?.sInfo?.length),
    meal: segment.meal.length > 0,
    baggage: segment.baggage.length > 0,
  };
  const anyAvailable = segments.some((s) => s.meal.length || s.baggage.length) ||
    seatMap.loading || Object.keys(seatMap.seats).length > 0;
  if (!anyAvailable) {
    return (
      <section className="panel addons">
        <h3><Icon name="bag" size={18} /> Seats, meals & baggage</h3>
        <p className="muted">This fare doesn't offer seat selection, meals or extra baggage in advance. Seats are assigned at check-in.</p>
      </section>
    );
  }

  const bySegment = value[segment.id] || {};
  const pick = (choice) => {
    const current = bySegment[active] || {};
    const next = { ...value, [segment.id]: { ...bySegment, [active]: { ...current, [kind]: choice || undefined } } };
    onChange(next);
    if (choice && kind === 'seat') {
      // Move on to the next traveller who still needs a seat on this leg.
      const nextTraveller = travellers.find((t) => t.index !== active && !next[segment.id][t.index]?.seat);
      if (nextTraveller) setActive(nextTraveller.index);
    }
  };
  const count = (k) => Object.values(bySegment).filter((c) => c[k]).length;

  return (
    <section className="panel addons">
      <h3><Icon name="bag" size={18} /> Seats, meals & baggage <span className="muted small addons-optional">Optional</span></h3>

      {segments.length > 1 && (
        <div className="addon-legs" role="tablist" aria-label="Flight leg">
          {segments.map((s) => (
            <button key={s.id} type="button" role="tab" aria-selected={s.id === segment.id}
              className={`addon-leg ${s.id === segment.id ? 'active' : ''}`} onClick={() => setSegmentId(s.id)}>
              <b>{s.label}</b><span>{s.flightNo}</span>
            </button>
          ))}
        </div>
      )}

      <div className="addon-kinds" role="tablist" aria-label="Add-on type">
        {kinds.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={kind === k}
            className={`addon-kind ${kind === k ? 'active' : ''}`} onClick={() => setKind(k)}>
            {label}{count(k) > 0 && <span className="addon-count">{count(k)}</span>}
          </button>
        ))}
      </div>

      <div className="addon-travellers" role="radiogroup" aria-label="Traveller">
        {travellers.map((t) => {
          const choice = bySegment[t.index]?.[kind];
          return (
            <button key={t.index} type="button" role="radio" aria-checked={active === t.index}
              className={`addon-traveller ${active === t.index ? 'active' : ''}`} onClick={() => setActive(t.index)}>
              <b>{t.label}</b>
              <span>{choice ? `${choice.label}${choice.amount ? ` · ${formatPrice(choice.amount)}` : ''}` : 'Not selected'}</span>
            </button>
          );
        })}
      </div>

      {kind === 'seat' && (
        seatMap.loading ? <p className="muted" role="status">Loading seat map…</p>
          : available.seat
            ? <SeatMap map={map} travellers={travellers} active={active} selections={bySegment} onPick={pick} />
            : <p className="muted">{seatMap.error || (seatsApplicable
              ? 'Seat selection is not available for this flight. Seats are assigned at check-in.'
              : 'The airline does not offer seat selection for this fare. Seats are assigned free at check-in.')}</p>
      )}
      {kind === 'meal' && (available.meal
        ? <OptionCards options={segment.meal} value={bySegment[active]?.meal} onPick={pick} noneLabel="No meal" />
        : <p className="muted">Meals can't be pre-booked on this flight.</p>)}
      {kind === 'baggage' && (available.baggage
        ? <OptionCards options={segment.baggage} value={bySegment[active]?.baggage} onPick={pick} noneLabel="Standard allowance" />
        : <p className="muted">{segment.journeyBaggage
          ? 'Extra baggage chosen on the first flight covers your whole journey.'
          : "Extra baggage can't be pre-booked on this flight."}</p>)}
    </section>
  );
}
